// Single source of truth for all score names, definitions, and casual phrasing.
// Ported from X:/projects/Remix of Tool/src/lib/scoreDefinitions.ts
// Sourced from OptiYield Variable Summary — Tables 4 & 5.

import type { GeoLevel } from './geoLevel'

export type ScoreKey =
  // Top-level indices
  | 'LVI'
  | 'PPI'
  | 'CDPI'
  | 'CMPI'
  | 'Gap'
  // Component sub-scores
  | 'CS'
  | 'RDS'
  | 'HCC'
  | 'HCO'
  | 'DTS'
  | 'CDS'
  | 'CES'
  | 'CTDS'
  | 'DDS'
  | 'SAM'
  | 'URBANITY'

export interface ScoreDef {
  abbrev: string
  fullName: string
  /** Short customer-facing display label (no abbreviation). */
  shortLabel: string
  /** One-sentence plain-English description for tooltips. */
  shortDesc: string
  /** Plain-English label for use in narrative text (no abbrev needed). */
  casualPhrase: string
  /** Numeric range as a display string (e.g. "0–100", "0–1", "−100 to +100"). */
  range: string
  /** What "higher" means in plain English. */
  higherMeans: string
  /**
   * T-537: the geography this score describes, as a structured value rather than the free-text
   * `geoScope` this replaces. That field was hardcoded to "Census-tract level" on every entry and
   * rendered nowhere — a second, wrong source of truth for the same fact. Presentation lives in
   * components/GeoLevelTag.tsx; see geoLevel.ts for what a level means.
   */
  level: GeoLevel
}

export const SCORE_DEFS: Record<ScoreKey, ScoreDef> = {
  LVI: {
    abbrev: 'LVI',
    fullName: 'Opportunity Score',
    shortLabel: 'Opportunity Score',
    shortDesc:
      'Overall investment opportunity for new EV charging at this site — blends the demand-vs-supply gap, pricing power, and site accessibility (addressable market) into one ranking.',
    casualPhrase: 'overall investment opportunity',
    range: '0–100',
    higherMeans: 'stronger investment case',
    // T-537 (Noreen batch-3 item 1b): this score is SITE-level — the copy used to say "in this
    // tract", which described the surrounding area rather than the thing being scored. The
    // tract LVI still exists and is shown separately as an explicitly labelled baseline
    // ("Census tract score" in the PDF, the tract-baseline row in ScorePanel), so the two
    // readings stay distinguishable.
    level: 'site',
  },
  PPI: {
    abbrev: 'PPI',
    fullName: 'Pricing Power Index',
    shortLabel: 'Pricing Power Index',
    shortDesc:
      'How much premium pricing the local market can support — driven by income, EV adoption, and limited competition. Not a measure of demand volume.',
    casualPhrase: 'pricing power',
    range: '0–100',
    higherMeans: 'more room to charge premium per-kWh rates',
    level: 'tract',
  },
  CDPI: {
    abbrev: 'CDPI',
    fullName: 'Demand Potential Index',
    shortLabel: 'Demand Potential Index',
    shortDesc:
      "Latent unmet demand potential for EV charging — combining corridor traffic, daytime destinations, and residents who can't charge at home.",
    casualPhrase: 'untapped demand potential',
    range: '0–100',
    higherMeans: 'more unmet charging demand potential',
    level: 'tract',
  },
  CMPI: {
    abbrev: 'CMPI',
    fullName: 'Market Pressure Index',
    shortLabel: 'Market Pressure Index',
    shortDesc:
      'How saturated the existing charging market is, relative to similar tracts. A score of 0 means no existing stations.',
    casualPhrase: 'competitive pressure',
    range: '0–100',
    higherMeans: 'more crowded market for new entrants',
    level: 'tract',
  },
  Gap: {
    abbrev: 'Gap',
    fullName: 'Demand Potential–Supply Gap',
    shortLabel: 'Gap Score',
    shortDesc:
      'Net imbalance between charging demand potential and existing supply. Positive means demand potential exceeds supply (opportunity); negative means oversaturated.',
    casualPhrase: 'demand potential vs. supply balance',
    range: '−100 to +100',
    higherMeans: 'more opportunity (demand potential outpaces supply)',
    level: 'tract',
  },
  // Component scores
  CS: {
    abbrev: 'CS',
    fullName: 'Corridor Score',
    shortLabel: 'Corridor Score',
    shortDesc:
      'How well-positioned this tract is along high-traffic EV travel corridors (interstates, ramps, AFC corridors).',
    casualPhrase: 'corridor access',
    range: '0–1',
    higherMeans: 'better corridor positioning',
    level: 'tract',
  },
  RDS: {
    abbrev: 'RDS',
    fullName: 'Retail-Destination Score',
    shortLabel: 'Retail Destination Score',
    shortDesc:
      'Attractiveness as a retail/commercial destination — combines retail jobs, dwell-time signal, and daytime population.',
    casualPhrase: 'retail destination strength',
    range: '0–1',
    higherMeans: 'stronger retail draw',
    level: 'tract',
  },
  HCC: {
    abbrev: 'HCC',
    fullName: 'Home-Charging Constrained Score',
    shortLabel: 'Home-Charging Constrained Score',
    shortDesc:
      "Share of residents who can't easily charge at home — dense, renter-heavy, multifamily neighborhoods. A high score means more reliance on public charging.",
    casualPhrase: 'home-charging constraint',
    range: '0–1',
    higherMeans: 'more residents who depend on public charging',
    level: 'tract',
  },
  HCO: {
    abbrev: 'HCO',
    fullName: 'Home-Charging Optimized Score',
    shortLabel: 'Home-Charging Optimized Score',
    shortDesc:
      'Share of residents who can charge at home — single-family, owner-occupied, vehicle-rich neighborhoods.',
    casualPhrase: 'home-charging suitability',
    range: '0–1',
    higherMeans: 'more residents able to charge in private driveways/garages',
    level: 'tract',
  },
  DTS: {
    abbrev: 'DTS',
    fullName: 'Dwell Time Score',
    shortLabel: 'Dwell Time Score',
    shortDesc:
      'How long visitors typically linger based on local job mix (retail, food, entertainment, healthcare). Higher = better fit for charging.',
    casualPhrase: 'typical visitor dwell time',
    range: '0–1',
    higherMeans: 'longer typical visits — better charging fit',
    level: 'tract',
  },
  // Sub-components (less commonly surfaced but defined for completeness)
  CDS: {
    abbrev: 'CDS',
    fullName: 'Captive Demand Potential Score',
    shortLabel: 'Captive Demand Potential Score',
    shortDesc:
      "Demand potential from residents who can't easily charge at home (renters, multifamily, long commuters). Sub-component of the Demand Potential Index.",
    casualPhrase: 'captive resident demand potential',
    range: '0–1',
    higherMeans: 'more captive residential demand potential',
    level: 'tract',
  },
  CES: {
    abbrev: 'CES',
    fullName: 'Corridor Exposure Score',
    shortLabel: 'Corridor Exposure Score',
    shortDesc:
      'Per-station measure of corridor positioning, aggregated across stations in the tract.',
    casualPhrase: 'corridor exposure',
    range: '0–100',
    higherMeans: 'stronger corridor exposure',
    level: 'tract',
  },
  CTDS: {
    abbrev: 'CTDS',
    fullName: 'Corridor-Transit Demand Potential Score',
    shortLabel: 'Corridor & Transit Demand Score',
    shortDesc:
      'Corridor and transit demand potential signal. Sub-component of the Demand Potential Index.',
    casualPhrase: 'corridor & transit demand potential',
    range: '0–1',
    higherMeans: 'stronger pass-through demand potential',
    level: 'tract',
  },
  DDS: {
    abbrev: 'DDS',
    fullName: 'Destination Demand Potential Score',
    shortLabel: 'Destination Demand Score',
    shortDesc:
      'Demand potential from workers, shoppers, and visitors passing through during the day. Sub-component of the Demand Potential Index.',
    casualPhrase: 'destination demand potential',
    range: '0–1',
    higherMeans: 'stronger daytime destination pull',
    level: 'tract',
  },
  SAM: {
    abbrev: 'SAM',
    fullName: 'Site Accessibility Modifier',
    shortLabel: 'Site Accessibility Score',
    shortDesc:
      'How physically accessible and commercially viable the tract is for new chargers — modulates the Opportunity Score.',
    casualPhrase: 'site accessibility',
    range: '0–1',
    higherMeans: 'more accessible / commercially viable site',
    level: 'tract',
  },
  URBANITY: {
    abbrev: 'Urbanity',
    fullName: 'Urbanity Score',
    shortLabel: 'Urbanity Score',
    shortDesc: "Numeric summary of the tract's urban-to-rural classification (RUCA 7-class).",
    casualPhrase: 'urbanity',
    range: '0–1',
    higherMeans: 'more urban',
    level: 'tract',
  },
}

/** Look up the score definition for a given ScoreKey. */
export const getScoreDef = (key: ScoreKey): ScoreDef => SCORE_DEFS[key]

/** Definitions ordered for the glossary (top-level indices first, then commonly-surfaced sub-scores). */
export const GLOSSARY_ORDER: ScoreKey[] = [
  'LVI',
  'PPI',
  'CDPI',
  'CMPI',
  'Gap',
  'CS',
  'RDS',
  'HCC',
  'HCO',
  'DTS',
]
